import axios, { AxiosResponse } from 'axios';
import { apiClient } from '../../apiClient';
import { FormType } from './formTypes';
import { FormSectionWithQuestions } from './formSections';
import { User } from '../../users';

// ---------------- TYPES ----------------
export interface FormTemplate {
    _id: string;
    title: string;
    description?: string;
    groupId: string;
    typeId: string;
    type?: FormType;
    supportDocumentId?: string;
    maxAttendees: number;
    allowSignature: boolean;
    allowPhotos: boolean;
    allowSingleResponsePerUser: boolean;
    isPublic: boolean;
    hasMaxAttendees: boolean;
    isSiteEnabled: boolean;
    status: number;
    version: number;
    publishedAt?: string;
    desactivatedAt?: string;
    createdAt: string;
    updatedAt?: string;
    createdBy: User;
    updatedBy?: User;
    desactivatedBy?: User;
    publishedBy?: User;
    supportDocumentFile?: { _id: string; fileKey: string; fileName: string; url: string };
}

export interface FormTemplateWithSections extends FormTemplate {
    sections: FormSectionWithQuestions[];
}

export interface FormTemplatesResponse {
    message: string;
    data: FormTemplate[];
}

export interface FormTemplateResponse {
    message: string;
    data: FormTemplate;
}

export interface FormTemplateWithSectionsResponse {
    message: string;
    data: FormTemplateWithSections;
}

// ---------- CREATE / UPDATE ----------

export interface CreateFormTemplateBody {
    title: string;
    description?: string;
    typeId: string;
    maxAttendees?: number;
    allowSignature?: boolean;
    allowPhotos?: boolean;
}

export interface UpdateFormTemplateBody {
    title?: string;
    description?: string;
    typeId?: string;
    maxAttendees?: number;
    allowSignature?: boolean;
    allowPhotos?: boolean;
    allowSingleResponsePerUser?: boolean;
    removeSupportDocument?: boolean;
    isPublic?: boolean;
    hasMaxAttendees?: boolean;
    isSiteEnabled?: boolean;
}

export interface GetByIdFormTemplateQueries {
    includeFiles?: boolean;
}

export interface GetLatestFormTemplatesQueries {
    activeOnly?: boolean;
    isPublic?: boolean;
}

// ---------------- CALLS API ----------------

// --- GET ---
export const getLatestTemplates = async (queries?: GetLatestFormTemplatesQueries): Promise<FormTemplatesResponse> => {
    try {
        const response: AxiosResponse<FormTemplatesResponse> = await apiClient.get('/form-templates/latest', { params: { ...queries } });
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export const getLatestPublishedTemplates = async (): Promise<FormTemplatesResponse> => {
    try {
        const response: AxiosResponse<FormTemplatesResponse> = await apiClient.get('/form-templates/latest/published');
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export const getTemplateById = async (id: string, queries?: GetByIdFormTemplateQueries): Promise<FormTemplateWithSectionsResponse> => {
    try {
        const response: AxiosResponse<FormTemplateWithSectionsResponse> = await apiClient.get(`/form-templates/${id}`, { params: { ...queries } });
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export const getTemplatesByGroupId = async (groupId: string): Promise<FormTemplatesResponse> => {
    try {
        const response: AxiosResponse<FormTemplatesResponse> = await apiClient.get(`/form-templates/group/${groupId}`);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// --- CREATE ---
export const createFormTemplate = async (data: CreateFormTemplateBody): Promise<FormTemplateResponse> => {
    try {
        const response: AxiosResponse<FormTemplateResponse> = await apiClient.post('/form-templates', data);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// --- UPDATE ---
export const updateFormTemplate = async (templateId: string, data: UpdateFormTemplateBody, file?: File): Promise<FormTemplateResponse> => {
    try {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
            if (value !== undefined) formData.append(key, value as any);
        });
        if (file) formData.append('supportDocument', file);

        const response: AxiosResponse<FormTemplateResponse> = await apiClient.patch(`/form-templates/${templateId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// --- VERSIONNING ---
export const duplicateFormTemplate = async (templateId: string, title?: string): Promise<FormTemplateResponse> => {
    try {
        const response: AxiosResponse<FormTemplateResponse> = await apiClient.post(`/form-templates/${templateId}/duplicate`, { title });
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export const createNewVersion = async (templateId: string): Promise<FormTemplateResponse> => {
    try {
        const response: AxiosResponse<FormTemplateResponse> = await apiClient.post(`/form-templates/${templateId}/new-version`);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

// --- PUBLISH / DEACTIVATE / DELETE ---
export const publishFormTemplate = async (templateId: string, withNotification = false): Promise<FormTemplateResponse> => {
    try {
        const response: AxiosResponse<FormTemplateResponse> = await apiClient.patch(
            `/form-templates/${templateId}/publish`,
            {},
            {
                params: { withNotification },
            }
        );
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export const desactivateFormTemplate = async (templateId: string): Promise<FormTemplateResponse> => {
    try {
        const response: AxiosResponse<FormTemplateResponse> = await apiClient.patch(`/form-templates/${templateId}/desactivate`);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};

export const deleteFormTemplate = async (templateId: string): Promise<FormTemplateResponse> => {
    try {
        const response: AxiosResponse<FormTemplateResponse> = await apiClient.delete(`/form-templates/${templateId}`);
        return response.data;
    } catch (error: any) {
        if (axios.isAxiosError(error) && error.response) throw new Error(error.response.data.message);
        throw new Error('An unexpected error occurred');
    }
};
